import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './HLD.css';
import { useAuth } from '../../hooks/useAuth';
import Loader from '../Loader/Loader';

import { getRequest, postRequest } from "../../Fetch/request";
import HldDraw from './HldDraw.jsx';
import HLDDocument from './HLDDocument';
import ClarificationPanel from '../Common/ClarificationPanel';
import FunctionalRequirementsAndScale from './FunctionalRequirementsAndScale';
import NonFunctionalRequirements from './NonFunctionalRequirements';
import SchemaDesigner from '../SchemaDesigner/SchemaDesigner';
import DeepDive from './DeepDive';
import DraggableNotes from '../Common/DraggableNotes';
import { HLD_STEPS, UI_TEXT, STORAGE_KEYS, API_ENDPOINTS, DEFAULTS, VALIDATION, STEP_NAME_BY_NUMBER } from '../../Utils/constants';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  Avatar,
  IconButton,
  Drawer,
  List,
  ListItem,

  ListItemText,


} from '@mui/material';
import {
  ArrowBack,
  Chat,
  Person,
  CheckCircle,
  RadioButtonUnchecked,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';




const STEPS = HLD_STEPS;

export default function HLDProblemDetails() {
  const navigate = useNavigate();
  const loc = useLocation();
  const problemId = new URLSearchParams(loc.search).get('problemId');
  const { isAuthenticated } = useAuth();
  const stepParam = new URLSearchParams(loc.search).get('step');


  const [problem, setProblem] = useState(null);
  const [problemData, setProblemData] = useState(null); // Problem data with functionalRequirements, scale, deepDiveQuestions
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [clarifications, setClarifications] = useState([]);
  // New state variables for 6-step flow
  const [functionalRequirements, setFunctionalRequirements] = useState([]);
  const [scale, setScale] = useState([]);
  const [nonFunctionalRequirements, setNonFunctionalRequirements] = useState([]);
  const [entities, setEntities] = useState([]);
  const [apis, setApis] = useState([]);
  const [deepDiveQuestions, setDeepDiveQuestions] = useState([]);
  const [documentVisible, setDocumentVisible] = useState(false);
  const [schemaDesign, setSchemaDesign] = useState({ entities: [], relationships: [] });
  const [apiDesign, setApiDesign] = useState({ entities: [], relationships: [] });
  
  // Track unsaved changes from diagram components
  const [hldDiagramUnsavedChanges, setHldDiagramUnsavedChanges] = useState(false);
  const [schemaDesignerUnsavedChanges, setSchemaDesignerUnsavedChanges] = useState(false);
  
  // Function to update schema design (for entities - kept for backward compatibility)
  const setSchemaDesignAndUpdate = async (val) => {
    setSchemaDesign(val);
    // Update localStorage immediately - use flat structure
    const attemptKey = `hld-attempt-${problemId}`;
    const currentData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
    
    // Store in flat structure (not nested in schemaDesign)
    if (val && val.entities) {
      currentData.schemaEntities = val.entities;
      currentData.schemaRelationships = val.relationships || [];
    }
    currentData.timestamp = Date.now(); // Update timestamp
    localStorage.setItem(attemptKey, JSON.stringify(currentData));
    
    // NOTE: Entities are saved via update-drawing API in SchemaDesigner component
    // This function only updates localStorage. The actual save happens in SchemaDesigner.saveSchema()
    // which calls /hld-attempt/update-drawing endpoint
  };
  
  // Function to update API design (similar to schema design)
  const setApiDesignAndUpdate = async (val) => {
    setApiDesign(val);
    // Update localStorage immediately - use flat structure
    const attemptKey = `hld-attempt-${problemId}`;
    const currentData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
    
    // Store APIs in flat structure
    if (val && val.entities) {
      currentData.apiEntities = val.entities;
      currentData.apiRelationships = val.relationships || [];
    }
    currentData.timestamp = Date.now(); // Update timestamp
    localStorage.setItem(attemptKey, JSON.stringify(currentData));
    
    // NOTE: APIs are saved via update-drawing API in SchemaDesigner component
    // This function only updates localStorage. The actual save happens in SchemaDesigner.saveSchema()
  };
  
  // Sidebar state management
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const savedWidth = localStorage.getItem(`hld-sidebar-width-${problemId}`);
    return savedWidth ? parseInt(savedWidth) : 350; // Default width
  });
  const [sidebarVisible, setSidebarVisible] = useState(() => {
    const savedVisibility = localStorage.getItem(`hld-sidebar-visible-${problemId}`);
    return savedVisibility ? JSON.parse(savedVisibility) : true; // Default visible
  });
  const [isResizing, setIsResizing] = useState(false);
  
  // Right sidebar state management
  const [rightSidebarVisible, setRightSidebarVisible] = useState(() => {
    const savedVisibility = localStorage.getItem(`hld-right-sidebar-visible-${problemId}`);
    const isVisible = savedVisibility ? JSON.parse(savedVisibility) : true; // Default visible
    return isVisible;
  });
  const [rightSidebarWidth, setRightSidebarWidth] = useState(() => {
    const savedWidth = localStorage.getItem(`hld-right-sidebar-width-${problemId}`);
    return savedWidth ? parseInt(savedWidth) : 400; // Default width
  });
  const [isResizingRight, setIsResizingRight] = useState(false);
  
  // Initialize step from localStorage or URL parameter or default to 0
  const [step, setStep] = useState(() => {
    // First check localStorage for saved step
    const savedStep = localStorage.getItem(`hld-step-${problemId}`);
    if (savedStep) {
      const stepFromStorage = parseInt(savedStep);
      // Migrate old step numbers: if step was 3 (APIs), 4 (HLD Diagram), or 5 (Deep Dives), adjust
      if (stepFromStorage === 3) return 2; // APIs: 3 -> 2
      if (stepFromStorage === 4) return 3; // HLD Diagram: 4 -> 3
      if (stepFromStorage === 5) return 4; // Deep Dives: 5 -> 4
      if (!isNaN(stepFromStorage) && stepFromStorage >= 0 && stepFromStorage <= 4) {
        return stepFromStorage;
      }
    }
    
    // Then check URL parameter
    if (stepParam !== null) {
      const stepFromUrl = parseInt(stepParam, 10);
      // Migrate old step numbers
      if (stepFromUrl === 3) return 2;
      if (stepFromUrl === 4) return 3;
      if (stepFromUrl === 5) return 4;
      if (!isNaN(stepFromUrl) && stepFromUrl >= 0 && stepFromUrl <= 4) {
        return stepFromUrl;
      }
    }
    
    return 0; // Default to step 0 (Functional Requirements & Scale)
  });

  // Update step and save to localStorage - block navigation if unsaved changes
  const setStepAndSave = (newStep) => {
    // Don't allow navigation if already on the target step
    if (newStep === step) {
      return;
    }
    
    const isDiagramStep = step === 3; // HLD Diagram step (was 4, now 3)
    
    // Block navigation if there are unsaved changes in HLD Diagram
    if (isDiagramStep && hldDiagramUnsavedChanges) {
      alert('You have not saved the HLD diagram. Please save your changes before moving to another step.');
      return; // Block step change
    }
    
    // Note: API step changes are automatically saved to localStorage, so no blocking needed
    
    setStep(newStep);
    localStorage.setItem(`hld-step-${problemId}`, newStep.toString());
  };
  
  // Update metadata function for HLD (similar to LLD)
  const updateMetadata = async (next = {}, retryCount = 0) => {
    const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
    if (!userId || !problemId) {
      throw new Error("Missing user id or problem ID");
    }
    
    // Only send fields that have been updated (present in 'next' parameter)
    const dataToSend = {
      userId: userId,
      hldProblemId: problemId
    };
    
    // Only include fields that are in 'next' (the fields that were actually updated)
    if (next.clarifications !== undefined) {
      dataToSend.clarifications = Array.isArray(next.clarifications) ? next.clarifications : [];
    }
    
    if (next.functionalRequirementsScale !== undefined) {
      dataToSend.functionalRequirementsScale = next.functionalRequirementsScale;
    }
    
    // Include conversation history if provided
    if (next.nonFunctionalRequirementsConversation !== undefined) {
      dataToSend.nonFunctionalRequirementsConversation = next.nonFunctionalRequirementsConversation;
    }
    
    if (next.apisConversation !== undefined) {
      dataToSend.apisConversation = next.apisConversation;
    }
    
    // If no fields to update, return early
    if (Object.keys(dataToSend).length === 2) { // Only userId and hldProblemId
      return { status: '200', message: 'No fields to update' };
    }
    
    try {
      const response = await postRequest(
        process.env.REACT_APP_URL + '/hld-attempt/update-metadata',
        dataToSend
      );
      if (!response || response.status !== '200') {
        throw new Error(`Invalid response: ${JSON.stringify(response)}`);
      }
      return response;
    } catch (err) {
      if (retryCount < 2) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return updateMetadata(next, retryCount + 1);
      }
      throw err;
    }
  };

  // Debounced update function to avoid too many API calls
  const debouncedUpdate = (() => {
    let timeoutId;
    return (data) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        try {
          const result = await updateMetadata(data);
        } catch (error) {
          // Error is already handled by the blocking logic in request.js
          // which shows the login prompt. Just silently catch here.
        }
      }, 1000); // 1 second debounce
    };
  })();

  // Save functionalRequirementsScale to metadata endpoint (only when user explicitly updates them)
  // Note: deepDiveQuestions are problem details (template), not user attempts
  // Problem details should NOT be saved via update-metadata API - they come from problem data
  const saveFunctionalRequirementsScaleAndDeepDives = async () => {
    const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
    if (!userId || !problemId) return;
    
    try {
      await postRequest(
        process.env.REACT_APP_URL + '/hld-attempt/update-metadata',
        {
          userId: userId,
          hldProblemId: problemId,
          functionalRequirementsScale: {
            functionalRequirements: functionalRequirements,
            scale: scale
          }
          // Note: Do NOT send deepDiveQuestions here - they are problem details, not user attempts
        }
      );
    } catch (err) {
      console.error('Failed to save functional requirements scale and deep dives:', err);
    }
  };

  // Handle clarifications updates
  const handleClarificationsChange = useCallback((newClarifications) => {
    setClarifications(newClarifications);
    
    // Update localStorage immediately
    const attemptKey = `hld-attempt-${problemId}`;
    const currentData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
    currentData.clarifications = newClarifications;
    currentData.timestamp = Date.now();
    localStorage.setItem(attemptKey, JSON.stringify(currentData));
    
    // Trigger metadata update with clarifications (debounced)
    debouncedUpdate({ clarifications: newClarifications });
  }, [problemId, debouncedUpdate]);

  // Simple data loading function - fetch all data first, then render
  const loadAllData = async () => {
    const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
    
    if (!problemId) {
      setLoading(false);
      return;
    }

    try {
      // Run all requests in parallel — including the fallback list endpoint so it's already
      // done if the primary problem fetch fails (no sequential waterfall).
      const [problemRes, aiStepsRes, attemptRes, allProblemsRes] = await Promise.all([
        getRequest(`${process.env.REACT_APP_URL}/hld-problems/${problemId}`),
        isAuthenticated ? getRequest(`${process.env.REACT_APP_URL}/ai-answers/hld-step/all?user_id=${encodeURIComponent(userId)}&hld_problem_id=${encodeURIComponent(problemId)}`) : Promise.resolve(null),
        isAuthenticated ? getRequest(`${process.env.REACT_APP_URL}/hld-attempt?userId=${userId}&hldProblemId=${problemId}`) : Promise.resolve(null),
        // Fallback in parallel — only used if primary fails, costs nothing extra when primary succeeds
        getRequest(`${process.env.REACT_APP_URL}/hld-problems?userId=${userId}`).catch(() => null)
      ]);

      // Process problem data
      let loadedProblem = null;
      if (problemRes?.status === '200' && problemRes?.response) {
        loadedProblem = problemRes.response;
        setProblem(loadedProblem);
        localStorage.setItem(`hld-problem-${problemId}`, JSON.stringify(loadedProblem));
      } else if (allProblemsRes?.status === '200' && Array.isArray(allProblemsRes.response)) {
        // Fallback already completed in parallel — no extra wait
        const foundProblem = allProblemsRes.response.find(p => p.problemId === problemId);
        if (foundProblem) {
          loadedProblem = foundProblem;
          setProblem(foundProblem);
          localStorage.setItem(`hld-problem-${problemId}`, JSON.stringify(foundProblem));
        }
      }

      // Process AI feedback data and store in localStorage
      if (aiStepsRes?.status === '200' && Array.isArray(aiStepsRes.response)) {
        const stepNameByNumber = STEP_NAME_BY_NUMBER.HLD;
        aiStepsRes.response.forEach((row) => {
          const stepName = stepNameByNumber[row.step_number];
          if (!stepName) return;
          const storageKey = `ai-feedback-HLD-${problemId}-${stepName}`;
          const fromServer = ((row.ai_answers || []).slice()).map((ans, idx) => ({
            id: `${row.id || 'srv'}_${idx}`,
            content: ans,
            timestamp: row.updated_date || row.created_date || new Date().toISOString()
          }));
          localStorage.setItem(storageKey, JSON.stringify(fromServer));
        });
      }

          // Process attempt data and store in localStorage
      const attemptKey = `hld-attempt-${problemId}`;
      
      if (attemptRes?.status === '200' && attemptRes.response) {
        const at = attemptRes.response;
        
        // Overwrite localStorage with backend data on refresh/initial load
        // Strategy: Replace localStorage completely with backend data (same as LLD)
        
        // Build attempt data structure from backend response
        const attemptDataToStore = {
          clarifications: at.clarifications || [],
          // Only store conversation fields, not arrays
          nonFunctionalRequirementsConversation: at.nonFunctionalRequirementsConversation || [],
          apisConversation: at.apisConversation || [],
          // Convert schemaDesign to flat structure if it exists
          schemaEntities: at.schemaDesign?.entities || at.schemaEntities || [],
          schemaRelationships: at.schemaDesign?.relationships || at.schemaRelationships || [],
          // Load API entities if they exist (from apiDesign or apiEntities)
          apiEntities: at.apiDesign?.entities || at.apiEntities || [],
          apiRelationships: at.apiDesign?.relationships || at.apiRelationships || [],
          // Store drawing in localStorage when loaded from backend (for caching)
          drawing: (at.hldDiagram || at.drawing) ? {
            shapes: (at.hldDiagram || at.drawing).shapes || [],
            connections: (at.hldDiagram || at.drawing).connections || []
          } : null,
          deepDiveConversations: at.deepDiveConversations || [],
          entities: at.entities || [],
          timestamp: Date.now()
        };
        
        // Preserve diagramSnapshot from existing localStorage (not stored on backend)
        const existingSnapshotData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
        if (existingSnapshotData.diagramSnapshot) {
          attemptDataToStore.diagramSnapshot = existingSnapshotData.diagramSnapshot;
        }

        // Overwrite localStorage with backend data
        localStorage.setItem(attemptKey, JSON.stringify(attemptDataToStore));

        // Dispatch custom event to notify child components that localStorage was updated
        window.dispatchEvent(new CustomEvent('localStorageUpdated', { 
          detail: { key: attemptKey, timestamp: attemptDataToStore.timestamp } 
        }));

        // Set state from backend data
        setClarifications(attemptDataToStore.clarifications);
        
        // Step 0: Functional Requirements & Scale - These are problem template data, NOT user attempts
        // They should be loaded from problem data, not attempt data
        // If not available from problem data yet, keep existing state (will be set when problem data loads)
        
        // Step 1: Non-Functional Requirements - conversation is loaded by component itself
        // Don't set nonFunctionalRequirements state - component loads from conversation
        
        // Step 2: APIs - using SchemaDesigner (similar to entities)
        setApiDesign({
          entities: attemptDataToStore.apiEntities || [],
          relationships: attemptDataToStore.apiRelationships || []
        });
        
        // Keep schemaDesign for backward compatibility (entities data)
        setSchemaDesign({
          entities: attemptDataToStore.schemaEntities || [],
          relationships: attemptDataToStore.schemaRelationships || []
        });
        setEntities(attemptDataToStore.entities || []);
      } else {
        // If no backend data (404, error, or empty response), check if we have local data
        // Don't clear localStorage if we have existing local data - preserve user work
        const existingLocalData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
        const hasLocalData = existingLocalData && (
          (existingLocalData.clarifications && existingLocalData.clarifications.length > 0) ||
          (existingLocalData.nonFunctionalRequirementsConversation && existingLocalData.nonFunctionalRequirementsConversation.length > 0) ||
          (existingLocalData.apisConversation && existingLocalData.apisConversation.length > 0) ||
          (existingLocalData.schemaEntities && existingLocalData.schemaEntities.length > 0) ||
          (existingLocalData.entities && existingLocalData.entities.length > 0) ||
          (existingLocalData.drawing && existingLocalData.drawing.shapes && existingLocalData.drawing.shapes.length > 0)
        );
        
        if (!hasLocalData) {
          // Only clear localStorage if there's no local data to preserve
          const emptyAttemptData = {
            clarifications: [],
            // Only store conversation fields, not arrays
            nonFunctionalRequirementsConversation: [],
            apisConversation: [],
            schemaEntities: [],
            schemaRelationships: [],
            // Don't store drawing in localStorage
            deepDiveConversations: [],
            entities: [],
            timestamp: Date.now()
          };

          // Preserve diagram snapshot even when clearing other data
          if (existingLocalData.diagramSnapshot) emptyAttemptData.diagramSnapshot = existingLocalData.diagramSnapshot;

          // Clear localStorage by storing empty structure
          localStorage.setItem(attemptKey, JSON.stringify(emptyAttemptData));
          
          // Dispatch custom event to notify child components that localStorage was updated
          window.dispatchEvent(new CustomEvent('localStorageUpdated', { 
            detail: { key: attemptKey, timestamp: emptyAttemptData.timestamp } 
          }));
          
          // Set state to empty values only if no local data
          setClarifications([]);
          // Don't set nonFunctionalRequirements - component loads from conversation
          setApiDesign({
            entities: [],
            relationships: []
          });
          setSchemaDesign({
            entities: [],
            relationships: []
          });
          setEntities([]);
        }
        // If we have local data, keep it and don't clear - preserve user work
      }

      setDataLoaded(true);
    } catch (error) {
      console.error('❌ Error loading HLD data:', error);
    } finally {
      // Only set loading to false after ALL data is processed
      setLoading(false);
    }
  };

  // Load data from localStorage immediately on mount (for instant display)
  useEffect(() => {
    if (!problemId) {
      setLoading(false);
      return;
    }

    // Load from localStorage immediately for instant display
    const attemptKey = `hld-attempt-${problemId}`;
    const localAttemptData = localStorage.getItem(attemptKey);
    
    if (localAttemptData) {
      try {
        const parsed = JSON.parse(localAttemptData);
        
        // Set state from localStorage immediately
        if (parsed.clarifications) setClarifications(parsed.clarifications);
        if (parsed.entities) setEntities(parsed.entities);
        if (parsed.apiEntities) {
          setApiDesign({
            entities: parsed.apiEntities || [],
            relationships: parsed.apiRelationships || []
          });
        }
        if (parsed.schemaEntities || parsed.entities) {
          setSchemaDesign({
            entities: parsed.schemaEntities || parsed.entities || [],
            relationships: parsed.schemaRelationships || []
          });
        }
      } catch (e) {
        // If localStorage data is invalid, continue with API load
      }
    }

    // Load problem data from localStorage if available
    const problemKey = `hld-problem-${problemId}`;
    const problemData = localStorage.getItem(problemKey);
    if (problemData) {
      try {
        const parsed = JSON.parse(problemData);
        if (parsed.functionalRequirements && parsed.functionalRequirements.length > 0) {
          setFunctionalRequirements(parsed.functionalRequirements);
        }
        if (parsed.scale && parsed.scale.length > 0) {
          setScale(parsed.scale);
        }
        if (parsed.deepDiveQuestions && parsed.deepDiveQuestions.length > 0) {
          setDeepDiveQuestions(parsed.deepDiveQuestions);
        }
      } catch (e) {
        // Continue with API load
      }
    }

    // Don't set loading to false here - keep it true until backend responds
    // The loadAllData function will set loading to false when it completes

    // Load from backend to sync
    loadAllData();
  }, [problemId, isAuthenticated]);

  // Extract problem data from the main problem API response
  useEffect(() => {
    if (problem) {
      // Extract the new structure: functionalRequirements, scale, deepDiveQuestions
      const problemDetails = problem.problemDetails || problem;
      
      const newProblemData = {
        functionalRequirements: problemDetails.functionalRequirements || problem.functionalRequirements || [],
        scale: problemDetails.scale || problem.scale || [],
        deepDiveQuestions: problemDetails.deepDiveQuestions || problem.deepDiveQuestions || []
      };

      setProblemData(newProblemData);
      
      // Set functional requirements and scale from problem data
      if (newProblemData.functionalRequirements.length > 0) {
        setFunctionalRequirements(newProblemData.functionalRequirements);
      }
      if (newProblemData.scale.length > 0) {
        setScale(newProblemData.scale);
      }
      if (newProblemData.deepDiveQuestions.length > 0) {
        setDeepDiveQuestions(newProblemData.deepDiveQuestions);
      }
      
      // Store problem details separately from user attempts
      // Problem details: questions, expected answers, functional requirements, scale (template data)
      // User attempts: user answers, AI feedback (user interaction data)
      if (problemId) {
        const problemKey = `hld-problem-${problemId}`;
        const problemDetailsToStore = {
          functionalRequirements: newProblemData.functionalRequirements || [],
          scale: newProblemData.scale || [],
          deepDiveQuestions: newProblemData.deepDiveQuestions || [], // Questions and expected answers
          timestamp: Date.now()
        };
        localStorage.setItem(problemKey, JSON.stringify(problemDetailsToStore));
        
        // Problem details are ONLY stored in hld-problem-${problemId}
        // Do NOT store problem details in hld-attempt-${problemId} - that's only for user attempts
      }
      
      // DO NOT call update-metadata API on refresh/load
      // Problem details are stored in hld-problem-${problemId}, not hld-problem-data
      // This should only be called when user explicitly saves or updates data
    }
  }, [problem]);

  // Add global mouse event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing]);

  // Add global mouse event listeners for right sidebar resizing
  useEffect(() => {
    if (isResizingRight) {
      document.addEventListener('mousemove', handleRightSidebarMouseMove);
      document.addEventListener('mouseup', handleRightSidebarMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleRightSidebarMouseMove);
        document.removeEventListener('mouseup', handleRightSidebarMouseUp);
      };
    }
  }, [isResizingRight]);

  if (loading) return <div style={{ marginTop: '100px' }}><Loader /></div>;
  // Only show "Problem not found" if loading is complete and problem is still null
  if (!loading && !problem) return <div style={{ textAlign: 'center', marginTop: 30 }}>Problem not found</div>;



  // Step Content - New 6-step flow
  let stepContent = null;
  const currentStepName = STEPS[step]?.label;
  
  if (step === 0) {
    // Step 1: Functional Requirements & Scale (read-only, pre-populated)
    stepContent = (
      <div className="fade-in">
        <FunctionalRequirementsAndScale
          functionalRequirements={functionalRequirements}
          scale={scale}
          problemTitle={problem?.title || ''}
          documentVisible={documentVisible}
          setDocumentVisible={setDocumentVisible}
        />
      </div>
    );
  } else if (step === 1) {
    // Step 2: Non-Functional Requirements
    stepContent = (
      <div className="fade-in">
        <NonFunctionalRequirements
          problemId={problemId}
          items={nonFunctionalRequirements}
          setItems={setNonFunctionalRequirements}
          problemTitle={problem?.title || ''}
          problemDescription={problem?.description || ''}
          documentVisible={documentVisible}
          setDocumentVisible={setDocumentVisible}
        />
      </div>
    );
  } else if (step === 2) {
    // Step 3: APIs - using SchemaDesigner (similar to entities)
    stepContent = (
      <div className="fade-in" style={{height:'calc(100vh - 64px)',width:'100%'}}>
        <SchemaDesigner 
          problemId={problemId} 
          schemaDesign={apiDesign}
          setSchemaDesign={setApiDesignAndUpdate}
          componentType="HLD"
          documentVisible={documentVisible}
          setDocumentVisible={setDocumentVisible}
          onUnsavedChangesChange={setSchemaDesignerUnsavedChanges}
          itemType="api"
          functionalRequirements={functionalRequirements}
          scale={scale}
          problemTitle={problem?.title || ''}
        />
      </div>
    );
  } else if (step === 3) {
    // Step 4: HLD Diagram
    stepContent = (
      <div className="fade-in">
        <HldDraw 
          key={`hld-draw-${problemId}`}
          problemId={problemId}
          problem={problem}
          documentVisible={documentVisible}
          setDocumentVisible={setDocumentVisible}
          onUnsavedChangesChange={setHldDiagramUnsavedChanges}
        />
      </div>
    );
  } else if (step === 4) {
    // Step 5: Deep Dives
    stepContent = (
      <div className="fade-in">
        <DeepDive
          problemId={problemId}
          problemTitle={problem?.title || ''}
          deepDiveQuestions={deepDiveQuestions}
          documentVisible={documentVisible}
          setDocumentVisible={setDocumentVisible}
        />
      </div>
    );
  }

  // Set the problemId in localStorage for HLDDocument to access
  if (problemId) {
    localStorage.setItem('current-hld-problemId', problemId);
  }

  // Sidebar resize handlers
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleMouseMove = (e) => {
    if (!isResizing) return;
    
    const newWidth = e.clientX;
    const minWidth = 250;
    const maxWidth = 600;
    
    if (newWidth >= minWidth && newWidth <= maxWidth) {
      setSidebarWidth(newWidth);
      localStorage.setItem(`hld-sidebar-width-${problemId}`, newWidth.toString());
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  // Right sidebar resize handlers
  const handleRightSidebarMouseDown = (e) => {
    e.preventDefault();
    setIsResizingRight(true);
  };

  const handleRightSidebarMouseMove = (e) => {
    if (!isResizingRight) return;
    
    const newWidth = window.innerWidth - e.clientX;
    const minWidth = 250;
    const maxWidth = 600;
    
    if (newWidth >= minWidth && newWidth <= maxWidth) {
      setRightSidebarWidth(newWidth);
      localStorage.setItem(`hld-right-sidebar-width-${problemId}`, newWidth.toString());
    }
  };

  const handleRightSidebarMouseUp = () => {
    setIsResizingRight(false);
  };

  // Sidebar toggle handler
  const toggleSidebar = () => {
    const newVisibility = !sidebarVisible;
    setSidebarVisible(newVisibility);
    localStorage.setItem(`hld-sidebar-visible-${problemId}`, JSON.stringify(newVisibility));
  };

  // Toggle right sidebar visibility
  const toggleRightSidebar = () => {
    const newVisibility = !rightSidebarVisible;
    setRightSidebarVisible(newVisibility);
    localStorage.setItem(`hld-right-sidebar-visible-${problemId}`, JSON.stringify(newVisibility));
  };

  // Get current step name
  const getCurrentStepName = () => {
    return STEPS[step]?.label || 'Unknown Step';
  };



  return (
    <Box sx={{ display: 'flex', bgcolor: '#f5f5f5', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Left Sidebar - Steps or Document */}
      {sidebarVisible && (
        <Box
          sx={{
            width: sidebarWidth,
            flexShrink: 0,
            bgcolor: '#fafafa',
            borderRight: '1px solid #e0e0e0',
            height: 'calc(100vh - 64px)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            transition: isResizing ? 'none' : 'width 0.2s ease'
          }}
        >
          {/* Header Section */}
          <Box sx={{ p: 1, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, mb: 0 }}>
              <IconButton onClick={() => navigate('/hld')} size="small">
                <ArrowBack />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
                {documentVisible ? 'HLD Document' : problem.title}
              </Typography>
            </Box>
          </Box>

          {/* Content Section - Steps or Document */}
          <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {documentVisible ? (
              // Document View
              <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
                <HLDDocument />
              </Box>
            ) : (
              // Steps View
              <List sx={{ 
                pt: 0.5, 
                overflow: 'auto', 
                flexGrow: 1,
                // Hide scrollbars but maintain scrolling functionality
                '&::-webkit-scrollbar': {
                  display: 'none'
                },
                '-ms-overflow-style': 'none',  // IE and Edge
                'scrollbarWidth': 'none',      // Firefox
              }}>
                {STEPS.map((stepItem, index) => {
                  const isActive = step === index;
                  
                  return (
                    <ListItem
                      key={stepItem.label}
                      button
                      onClick={() => setStepAndSave(index)}
                      sx={{
                        mx: 1,
                        mb: 0.5,
                        borderRadius: 2,
                        bgcolor: 'transparent',
                        '&:hover': {
                          bgcolor: '#f5f5f5'
                        },
                        overflow: 'hidden',
                        boxSizing: 'border-box'
                      }}
                    >
                      <ListItemText
                        primary={stepItem.label}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontWeight: isActive ? 'bold' : 'normal',
                            color: isActive ? '#008080' : '#333'
                          }
                        }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
          
          {/* Resize Handle */}
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              width: '4px',
              height: '100%',
              cursor: 'col-resize',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: '#008080',
                opacity: 0.3
              },
              '&:active': {
                backgroundColor: '#008080',
                opacity: 0.5
              }
            }}
            onMouseDown={handleMouseDown}
          />
        </Box>
      )}

      {/* Left Sidebar Toggle Button */}
              <Box
          sx={{
            position: 'absolute',
            left: sidebarVisible ? sidebarWidth -13 : 0,
            top: '13%',
            zIndex: 1001,
            transition: 'left 0.2s ease',
            display: 'flex',
            alignItems: 'center'
          }}
        >
        {/* Arrow Icon on the Line */}
        <Box
          sx={{
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            border: '1px solid #e0e0e0',
            borderRadius: '50%',
            color: '#008080',
            fontSize: '16px',
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#008080',
              color: 'white',
              borderColor: '#008080',
              transform: 'scale(1.1)'
            }
          }}
          onClick={toggleSidebar}
        >
          {sidebarVisible ? <ChevronLeft fontSize="inherit" /> : <ChevronRight fontSize="inherit" />}
        </Box>
      </Box>

      {/* Main Workspace Area */}
      <Box sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        '& *': {
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          '-ms-overflow-style': 'none',
          'scrollbarWidth': 'none'
        }
      }}>
        {/* Content Area */}
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          {stepContent}
        </Box>
      </Box>

      {/* Right Sidebar Toggle Button */}
              <Box
          sx={{
            position: 'absolute',
            right: rightSidebarVisible ? rightSidebarWidth -13 : 0,
            top: '13%',
            zIndex: 1001,
            transition: 'right 0.2s ease',
            display: 'flex',
            alignItems: 'center'
          }}
        >
        {/* Arrow Icon on the Line */}
        <Box
          sx={{
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            border: '1px solid #e0e0e0',
            borderRadius: '50%',
            color: '#008080',
            fontSize: '16px',
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#008080',
              color: 'white',
              borderColor: '#008080',
              transform: 'scale(1.1)'
            }
          }}
          onClick={toggleRightSidebar}
        >
          {rightSidebarVisible ? <ChevronRight fontSize="inherit" /> : <ChevronLeft fontSize="inherit" />}
        </Box>
      </Box>

      {/* Right Sidebar - Clarification Chat */}
      {rightSidebarVisible && (
        <Box
          sx={{
            width: rightSidebarWidth,
            flexShrink: 0,
            bgcolor: '#fafafa',
            borderLeft: '1px solid #e0e0e0',
            height: 'calc(100vh - 64px)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            transition: isResizingRight ? 'none' : 'width 0.2s ease'
          }}
        >
          {/* Right Sidebar Header */}
          <Box sx={{ 
            p: 1.8, 
            flexShrink: 0,
            bgcolor: '#f8f9fa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 'bold', 
              color: '#333',
              fontSize: '1.1rem',
              lineHeight: 1.2
            }}>
              Ask questions to clarify requirements
            </Typography>
          </Box>

          {/* Right Sidebar Content */}
          <Box sx={{ flexGrow: 1, overflow: 'hidden', p: 2 }}>
            {dataLoaded ? (
              <ClarificationPanel 
                problemId={problemId}
                problemTitle={problem?.title || ''}
                componentType="HLD"
                onClarificationsChange={handleClarificationsChange}
                initialConversation={clarifications}
              />
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <Typography variant="body2" color="text.secondary">
                  Loading clarifications...
                </Typography>
              </Box>
            )}
          </Box>

          {/* Resize Handle */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: '4px',
              height: '100%',
              cursor: 'col-resize',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: '#008080',
                opacity: 0.3
              },
              '&:active': {
                backgroundColor: '#008080',
                opacity: 0.5
              }
            }}
            onMouseDown={handleRightSidebarMouseDown}
          />
        </Box>
      )}

      {/* Draggable Notes Component - Persistent across all steps */}
      {problemId && problem && <DraggableNotes problemId={problemId} problemType="HLD" problem={problem} />}

    </Box>
  );
}